import React from "react";
import { ConfigCatProvider, createFlagOverridesFromMap, OverrideBehaviour } from "configcat-react";

/**
 * @typedef {Object} ConfigCatUser
 * @property {string} identifier
 * @property {Object<string, string>} [custom] Name, Role, MakeFK, MakeName, MakeCode, InventoryPK, Dealer
 */

/**
 * @returns {ConfigCatUser}
 */
const getUser = function getUser() {
    return window.configCatUser || { identifier: "anonymous" };
};

/**
 * Same shape check the ConfigCat SDK runs before creating a client. A key that fails it
 * makes the SDK throw, so we fall back to local-only (every flag returns its default) instead.
 * @param {string} sdkKey
 * @returns {boolean}
 */
const isValidSdkKey = function isValidSdkKey(sdkKey) {
    return /^(configcat-sdk-1\/)?[^/]{22}\/[^/]{22}$/.test(sdkKey || "");
};

/**
 * Wraps a widget's root component with the ConfigCat provider.
 * sdkKey/user come from window globals set server-side in Index.cshtml.cs.
 * @template {React.ComponentType<any>} C
 * @param {C} RootComponent
 * @returns {C}
 */
export default function withFeatureFlagProvider(RootComponent) {
    const sdkKey = window.configCatSdkKey;
    const hasValidSdkKey = isValidSdkKey(sdkKey);
    const options = hasValidSdkKey
        ? { defaultUser: getUser() }
        : { defaultUser: getUser(), flagOverrides: createFlagOverridesFromMap({}, OverrideBehaviour.LocalOnly) };

    const WithFeatureFlagProvider = function WithFeatureFlagProvider(props) {
        return (
            <ConfigCatProvider sdkKey={hasValidSdkKey ? sdkKey : "local-only"} options={options}>
                <RootComponent {...props} />
            </ConfigCatProvider>
        );
    };

    return WithFeatureFlagProvider;
}
